import { selectAppliedInferenceSet, selectClient } from '@client/model/selectors';
import { calculateMonthsBetweenYears } from '@shared';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { calcSelfFundingProjectedCostCoverage } from '@shared/calcFundingSelfDerivatives';
import { produce } from 'immer';
import { AccountDetailFormState } from '@client/hooks/useAccountDetailForm';

/**
 * Custom hook that provides common LTC planning calculation parameters.
 * Centralizes the logic for getting years to LTC, contribution months, and cost projections.
 * Also includes self-funding calculation logic.
 */
export function useLtcPlanningCalculations() {
  const client = useSelector(selectClient);
  const appliedInferenceSet = useSelector(selectAppliedInferenceSet);

  const { ltcAtYear, yearsTillLtc: yearsTillLtcRaw } = appliedInferenceSet;
  const yearsTillLtc = Math.max(yearsTillLtcRaw, 0);

  const projectedLtcCost = client.allPhaseCosts.allPhaseInflatedProfessionalShareCost;
  const projectedLtcCostWithoutInflation = client.allPhaseCosts.allPhaseProfessionalShareCost;

  const contributionStartYear = new Date().getFullYear();
  const contributionEndYear = ltcAtYear;
  const contributionMonthCount =
    calculateMonthsBetweenYears(contributionStartYear, contributionEndYear) || 0;

  /**
   * Calculate self-funding source and projected value
   * Uses the same calculation logic as the backend via calcSelfFundingProjectedCostCoverage
   */
  const calculateSelfFunding = useMemo(() => {
    return (formState: AccountDetailFormState | SelfFundingSource) => {
      // Create selfFundingSource object from formState
      const selfFundingSource: SelfFundingSource =
        'hasSelfFunding' in formState
          ? (formState as SelfFundingSource)
          : {
              hasSelfFunding: true,
              existingAssetsValue: formState.existingAssetsValue,
              monthlyContributionAmount: formState.monthlyContributionAmount,
              annualRateOfReturn: formState.annualRateOfReturn,
              gainsTaxRate: formState.gainsTaxRate,
              contributionStartYear: new Date().getFullYear(),
              contributionEndYear: new Date().getFullYear() + yearsTillLtc,
              monthlyIncome: null,
              contributionMonthCount: contributionMonthCount,
            };

      // Calculate projected value using the shared calculation function
      const existingAssetsValue = selfFundingSource.existingAssetsValue;
      const annualRateOfReturn = selfFundingSource.annualRateOfReturn;
      const gainsTaxRate = selfFundingSource.gainsTaxRate;

      let projectedValue = 0;
      if (existingAssetsValue !== null && annualRateOfReturn !== null && gainsTaxRate !== null) {
        // Construct a minimal partial Client object with required structure
        const partialClient = {
          fundingSources: {
            selfFunding: selfFundingSource,
          },
          allPhaseCosts: {
            allPhaseCareMonthsNeeded: 0, // Not needed for projected value calc
          },
          appliedInferenceSet: {
            yearsTillLtc: yearsTillLtc,
          },
        } as Partial<Client>;

        // Use produce to call the function with a mutable draft
        const result = produce(partialClient, (draft: any) => {
          calcSelfFundingProjectedCostCoverage(draft);
        }) as Partial<Client>;

        // Extract and return the calculated value
        projectedValue = result.fundingSources?.selfFunding.selfFundingTotalValue ?? 0;
      }

      return { selfFundingSource, projectedValue };
    };
  }, [yearsTillLtc, contributionMonthCount]);

  return {
    yearsTillLtc,
    yearsTillLtcRounded: Math.round(yearsTillLtc),
    ltcAtYear,
    projectedLtcCost,
    projectedLtcCostWithoutInflation,
    contributionStartYear,
    contributionEndYear,
    contributionMonthCount,
    calculateSelfFunding,
  };
}
